import React from "react";
import { Button } from "react-bootstrap";
import {
  KTMScreen,
  ActionEvent,
  RenderEvent,
  renderScreen,
  Card,
  Line,
  Panel,
  Column,
} from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import { DataGrid, IDataGridLayout, EmptyState } from "ktm-ui";
import BencatRecordScreen from "planadmin/bencat/Record";
import BencatNewScreen from "planadmin/bencat/New";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import axios from "axios";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface IdsBenefitCategories {
    Id:string;
    Name:string;
    Description:string;
    Policy_Id:string;
    FlexSingleLimit:number;
    FlexCoupleLimit:number;
    FlexFamilyLimit:number;
    ExcludeTeladoc: boolean;
    ExcludeCorehealth: boolean;
    HasHSA: boolean;
    HasLSA: boolean;
    HasAccrual:boolean;
    HasEnhancedBenefit:boolean;
};

interface IdsBenefits {
  Id: string;
  HasFlex: boolean;
}

interface IdsExpenseTypes {
  Id: string;
  Policy_Id: string;
  Limit_Id: string;
  ExpenseType_Id: string;
  CoverageRate: number;
  AccountType: string;
  Code: string;
  ShortName: string;
  Description: string;
}
export default KTMScreen(
  { name: "planadmin.policy.benefit-categories" },
  class extends React.Component<{
    policy_id: string;
    hasTeladoc: boolean;
    hasCorehealth: boolean;
    enable_wellness: boolean;
  }> {
    data: {
      dsBenefitCategories: IdsBenefitCategories[];
      dsBenefits: IdsBenefits;
      dsExpenseTypes: IdsExpenseTypes[];
    };

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsBenefitCategories";
      await KTMApi.start(screen, {
        "@policy_id": this.props.policy_id,
      });
      screen.title = "Benefit categories";
    }

    main({ field, draw, action }: RenderEvent) {
      return (
        <Panel>
          <Column>
            {draw(this.default_view)}
            {draw(this.request_edits)}
          </Column>
        </Panel>
      );
    }

    default_view({field,draw,action,collection}:RenderEvent){
        let layout: IDataGridLayout = {
            columns: [
              { name: "Name", label: "Name", type: "character", width: 300 },
              { name: "IsHidden", label: "Is Hidden", type: "logical" },
            ],
            sort: [{ name: "Name" }],
          };
    
          if (this.props.hasTeladoc) {
            layout.columns.push({
              name: "ExcludeTeladoc",
              label: "Exclude Teladoc",
              type: "logical",
            });
          }

      if (this.props.hasCorehealth) {
        layout.columns.push({
          name: "ExcludeCorehealth",
          label: "Exclude Corehealth",
          type: "logical",
        });
      }

      let hasEnhancedBenefit = this.data.dsBenefitCategories.some((cat:IdsBenefitCategories) => cat.HasEnhancedBenefit);
      if (hasEnhancedBenefit) {  
          layout.columns.push({name:"HasEnhancedBenefit",label:"Has Enhanced Benefit",type:"logical"});
      }
      
      let hasAccrual = this.data.dsBenefitCategories.some((cat:IdsBenefitCategories) => cat.HasAccrual);
      if (hasAccrual) {
          layout.columns.push({name:"HasAccrual",label:"Scheduled Credits Enabled",type:"logical"});
      }


        if (this.data.dsBenefits.HasFlex) {
            layout.columns.push(
                { name: "FlexSingleLimit", label: "Flex Single Limit", type: "decimal" },
                { name: "FlexCoupleLimit", label: "Flex Couple Limit", type: "decimal" },
                { name: "FlexFamilyLimit", label: "Flex Family Limit", type: "decimal" }
            );
        }

        layout.columns.push({
            name: "actions",
            label: "Download Expense Types",
            width: 300,
            render: (row) => (
              <div style={{ display: "flex", gap: "10px", paddingBottom: "12px" }}>
                {row.HasHSA && <Button
                  style={{
                    fontSize: "12px",
                    padding: "2px 6px",
                    margin: 0,
                    height: "24px",
                    minWidth: "unset",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.downloadHSA(row);
                  }}
                >
                  HSA
                </Button>}
                {row.HasLSA && <Button
                  style={{
                    fontSize: "12px",
                    padding: "2px 6px",
                    margin: 0,
                    height: "24px",
                    minWidth: "unset",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.downloadLSA(row);
                  }}
                >
                  LSA
                </Button>}
              </div>
            ),
          });
          
        return (
            <DataGrid 
                collection={collection("@dsBenefitCategories")} 
                layout={layout} 
                onRowClick={action(this.show_record)}
            />
        );
    }

    downloadHSA = async (row) => {
      try {
        let documentDefinition = await this.generatePDFDocumentForHSA(row);
        if (documentDefinition) {
          let fileName = `${row.Name} HSA Expense Types.pdf`;
          pdfMake.createPdf(documentDefinition).download(fileName);
        } else {
          console.warn("No document definition returned");
        }
      } catch (error) {
        console.error("Error generating HSA PDF:", error);
      }
    };

    downloadLSA = async (row) => {
      try {
        const documentDefinition = await this.generatePDFDocumentForLSA(row);
        if (documentDefinition) {
          const fileName = `${row.Name} LSA Expense Types.pdf`;
          pdfMake.createPdf(documentDefinition).download(fileName);
        } else {
          console.warn("No document definition returned");
        }
      } catch (error) {
        console.error("Error generating LSA PDF:", error);
      }
    };

    generatePDFDocumentForHSA = async (row) => {
      try {
        const result = await axios.get("/api/PlanAdmin/getexpensetypes", {
          params: {
            policyId: this.props.policy_id,
            limitId: row.Id,
          },
        });

        const expenseTypes = result?.data?.dsExpenseTypes;

        if (!Array.isArray(expenseTypes)) {
          console.error("dsExpenseTypes is not an array:", result.data);
          return null;
        }
        const tableBody = [];
        expenseTypes
          .filter(
            (expense) => expense.accountType === "HSA" && expense.code !== "OTC"
          )
          .forEach((expense) => {
            tableBody.push([
              {
                text: expense.shortName,
                fillColor: "#ADD8E6",
                colSpan: 2,
                bold: true,
              },
              {},
            ]);

            const descriptionLines = expense.description
              .split("\\n")
              .map((line) => line.trim());
            tableBody.push([
              {
                stack: descriptionLines,
                colSpan: 2,
                fontSize: 12,
              },
              {},
            ]);
          });

        const introductoryText = [
            { text: 'The Health Spending Account', style: 'header' },
            'Use this tax-free account to get coverage for a wide range of health and dental expenses.',
            { text: 'Note: If you already have coverage for these items through a traditional insurance group plan, always use them first. Your health spending account can be used to pay for any portions the group plan did not pay, deductibles/co-payments and items your group plan doesn\'t cover at all. In your submission to us include their "Explanation of Benefits," the detailed document that confirms how much the insurance carrier has paid towards an expense (even if it is 0). Online printout copies are acceptable.', style: 'note'},
            { text: 'If you don’t have a traditional insurance group plan, submit to National HealthClaim directly.', style: 'subnote'},
            { text: 'What can I claim?', style: 'subheader' },
            { text: 'See the coverage charts below.', style: 'chartnote'},
            { text: 'Expenses can be from inside or outside Canada. National HealthClaim is as diligent as possible in compiling and updating this list. However, National HealthClaim does not guarantee that this information is up to date. If there is a discrepancy between this list and the list of Canada Revenue Agency (CRA) eligible medical expenses, the CRA list will always take precedence.', style: 'chartnote'},
        ];

        return {
          content: [
            ...introductoryText,
            {
              table: {
                headerRows: 0,
                widths: ["*", "*"],
                body: tableBody,
              },
            },
          ],
          styles: {
            header: {
              fontSize: 18,
              bold: true,
              alignment: "center",
              margin: [0, 0, 0, 20],
            },
            note: {
              margin: [40, 10, 0, 10],
              fontSize: 12,
            },
            subnote: {
              margin: [40, 0, 0, 10],
              fontSize: 12,
            },
            subheader: {
              fontSize: 16,
              bold: true,
              margin: [0, 10, 0, 10],
            },
            chartnote: {
              margin: [0, 0, 0, 10],
              fontSize: 12,
            },
          },
        };
      } catch (error) {
        console.error("Error generating HSA PDF:", error);
        return null;
      }
    };

    generatePDFDocumentForLSA = async (row) => {
      try {
        const result = await axios.get("/api/PlanAdmin/getexpensetypes", {
          params: {
            policyId: this.props.policy_id,
            limitId: row.Id,
          },
        });

        const expenseTypes = result?.data?.dsExpenseTypes;

        if (!Array.isArray(expenseTypes)) {
          console.error("dsExpenseTypes is not an array:", result.data);
          return null;
        }
        const allowDependents = expenseTypes[0]?.allowLsadependentClaims === true;
        const lsaDep = {
          text: allowDependents
            ? "Your lifestyle spending account extends coverage to both yourself and the eligible dependents added to your account."
            : "Your lifestyle spending account is an employee-only benefit and does not cover expenses for dependents added to your account.",
          margin: [0, 10, 0, 10],
        };
        const tableBody = [];

        expenseTypes
          .filter((expense) => expense.accountType === "LSA")
          .sort((a, b) => {
            if (a.Code === "*PAY AS SUBMITTED") return 1;
            if (b.Code === "*PAY AS SUBMITTED") return -1;
            return a.shortName.localeCompare(b.shortName);
          })
          .forEach((expense) => {
            tableBody.push([
              {
                text: expense.shortName,
                fillColor: "#90EE90",
                colSpan: 2,
                bold: true,
              },
              {},
            ]);

            const descriptionLines = expense.description
              .split("\\n")
              .map((line) => line.trim());

            tableBody.push([
              {
                stack: descriptionLines,
                colSpan: 2,
                fontSize: 12,
              },
              {},
            ]);
          });

        if (tableBody.length === 0) {
          console.error("No HSA expense types found for this category.");
          return null;
        }
        
        const isWSA = this.props.enable_wellness;
        const ACCOUNT = isWSA ? "Wellness" : "Lifestyle";
        const ABBR = isWSA ? "WSA" : "LSA";

        const introductoryText = [
          { text: `The ${ACCOUNT} Spending Account`, style: "header", margin: [0, 0, 0, 10] },
          {
            text:
              "This benefit is not insurance but a spending account that reimburses you for certain wellness expenses. This means you pay for the expenses first, and then submit receipts to us, and we process them on behalf of your company.",
            margin: [0, 0, 0, 10],
          },
          {
            text: `Note: The ${ACCOUNT} Spending Account (${ABBR}) is reimbursed by the date the payment is made for each expense.`,
            style: "note",
            margin: [0, 0, 0, 10],
          },
          {
            text:
              "To claim lifestyle expenses you must submit the detailed invoice showing who the expense is for, what is being purchased, purchase date, total cost, and a confirmation of payment including payment date.",
          },
          lsaDep,
        ];

        return {
          content: [
            ...introductoryText,
            {
              table: {
                headerRows: 0,
                widths: ["*", "*"],
                body: tableBody,
              },
            },
          ],
          styles: {
            header: {
              fontSize: 18,
              bold: true,
              alignment: "center",
              margin: [0, 0, 0, 20],
            },
          },
          footer: (currentPage, pageCount) => ({
            text: `Page ${currentPage} of ${pageCount}`,
            alignment: "center",
            margin: [0, 10, 0, 0],
            fontSize: 10,
          }),
        };
      } catch (error) {
        console.error("Error fetching expense types:", error);
        return null;
      }
    };

    request_edits({ field, draw, action }: RenderEvent) {
      return (
        <div style={{ marginTop: "100px" }}>
          <Card label="Request Edits">
            <Line>
              <div>
                Click on the names in the chart above for more information on
                each benefit category, and to view/edit the limits. To add or
                remove a benefit category from your plan, email{" "}
                <a href="#" style={{ color: "#4987ef" }}>
                  support@nationalhealthclaim.com
                </a>{" "}
                with your request.
              </div>
            </Line>
          </Card>
        </div>
      );
    }

    empty_state({ field, draw, action }: RenderEvent) {
      return (
        <EmptyState label="No benefit categories created yet." icon="folder"/>
      );
    }

    async show_record({ screen }: ActionEvent) {
      screen.browse(<BencatRecordScreen bencat_id={screen.getValue("Id")} />, {
        label: "Benefit category",
      });
    }

    async refresh_row({ screen, eventValue }: ActionEvent) {
      await KTMApi.refreshRow(screen, {
        collection: "@dsBenefitCategories",
        rowid: eventValue,
      });
    }

    async new_record({ screen }: ActionEvent) {
      await screen.openDialog(
        <BencatNewScreen policy_id={this.props.policy_id} />,
        { onRecordChanged: this.after_new }
      );
    }

    async after_new({ screen, eventValue }: ActionEvent) {
      screen.showLayout(this.main);
      await KTMApi.refreshRow(screen, {
        collection: "@dsBenefitCategories",
        rowid: eventValue,
      });
      screen.locate("@dsBenefitCategories", eventValue);
      await screen.triggerAction(this.show_record);
    }
  }
);
